import type { SajuAnalysis } from '../types/input';
import type { MarriagePotential } from '../types/compatibility';
import type { TenGod } from '../types/ten-gods';
import { getStemElement } from '../core/five-elements';

// 십신 카테고리 (countStem에서 직접 배열 리터럴로 사용)

const CONFLICT_TYPES = new Set(['육충', '삼형', '상형', '자묘형', '자형', '파', '해']);

/**
 * 개인의 혼인 그릇(결혼 안정성)을 평가한다.
 *
 * 자평 전통에서 궁합 전에 "각자의 원국에서 재성/관성의 동태,
 * 배우자궁의 길흉을 먼저 보아 혼인 그릇을 평가"하는 원칙을 구현.
 */
export function analyzeMarriagePotential(analysis: SajuAnalysis): MarriagePotential {
  const tg = analysis.tenGods;
  const riskFlags: string[] = [];
  let score = 100;

  // 1. 재성 상태 (남자 배우자성)
  const jaeJeong = countStem(tg, ['정재']);
  const jaePyeon = countStem(tg, ['편재']);
  const jaeseongStatus = classifyStatus(jaeJeong, jaePyeon);
  if (jaeseongStatus === '혼잡') { riskFlags.push('재성혼잡'); score -= 10; }
  if (jaeseongStatus === '과다') { riskFlags.push('재성과다'); score -= 5; }
  if (jaeseongStatus === '부재' && analysis.input.gender === 'male') { riskFlags.push('재성부재(남)'); score -= 10; }

  // 2. 관성 상태 (여자 배우자성)
  const gwanJeong = countStem(tg, ['정관']);
  const gwanPyeon = countStem(tg, ['편관']);
  const gwanseongStatus = classifyStatus(gwanJeong, gwanPyeon);
  if (gwanseongStatus === '혼잡') { riskFlags.push('관살혼잡'); score -= 15; }
  if (gwanseongStatus === '과다') { riskFlags.push('관성과다'); score -= 5; }
  if (gwanseongStatus === '부재' && analysis.input.gender === 'female') { riskFlags.push('관성부재(여)'); score -= 10; }

  // 3. 배우자궁(일지) 형충파해
  const spousePalaceConflicts: string[] = [];
  for (const r of analysis.relations) {
    if (r.pillars.includes('day') && CONFLICT_TYPES.has(r.type)) {
      spousePalaceConflicts.push(r.description ?? r.type);
      score -= r.type === '육충' ? 15 : (r.type === '삼형' || r.type === '상형' ? 10 : 5);
    }
  }

  // 4. 구조적 파격 리스크 (강: 관살혼잡, 재다신약)
  for (const reason of analysis.gyeokGuk.pagyeokReasons) {
    if (reason === '관살혼잡' && !riskFlags.includes('관살혼잡')) {
      riskFlags.push('관살혼잡(파격)');
      score -= 10;
    } else if (reason === '재다신약') {
      riskFlags.push('재다신약');
      score -= 10;
    }
  }

  // 5. 패턴 리스크: 상관견관 (중간 강도)
  // 파격 판정 = 엔진이 이미 "상관의 정관 극을 제어할 통관이 불충분"으로 판단한 결과
  // 여명에서 상관견관 파격이면 부부 불화·갈등 패턴 리스크
  if (analysis.input.gender === 'female' &&
      analysis.gyeokGuk.pagyeokReasons.includes('상관견관')) {
    riskFlags.push('상관견관');
    score -= 7; // 관살혼잡(-15)보다 낮은 중간 감점
  }

  // 6. 비겁 과다
  const dmElement = getStemElement(analysis.dayMaster);
  const totalDist = Object.values(analysis.ohaengDistribution).reduce((s, v) => s + v, 0);
  const dmRatio = totalDist > 0 ? analysis.ohaengDistribution[dmElement] / totalDist : 0;
  if (dmRatio >= 0.40) {
    riskFlags.push('비겁과다');
    score -= 10;
  }

  score = Math.max(score, 0);

  const flagStr = riskFlags.length > 0 ? riskFlags.join(', ') : '없음';
  const description = `혼인그릇: ${score}점 (리스크: ${flagStr})`;

  return {
    jaeseongStatus,
    gwanseongStatus,
    spousePalaceConflicts,
    riskFlags,
    score,
    description,
  };
}

/** 천간 십신 + 지장간 십신에서 특정 십신 수를 센다 */
function countStem(tg: SajuAnalysis['tenGods'], targets: TenGod[]): number {
  let count = 0;
  // 천간 (년/월/시)
  if (targets.includes(tg.year.tenGod)) count++;
  if (targets.includes(tg.month.tenGod)) count++;
  if (tg.hour && targets.includes(tg.hour.tenGod)) count++;
  // 지장간: 모든 기둥에서 존재 여부 확인 (기둥당 1개만 카운트)
  for (const pos of ['year', 'month', 'day', 'hour'] as const) {
    const hidden = tg.hiddenStems[pos];
    if (hidden) {
      for (const h of hidden) {
        if (targets.includes(h.tenGod)) { count++; break; }
      }
    }
  }
  return count;
}

function classifyStatus(jeong: number, pyeon: number): MarriagePotential['jaeseongStatus'] {
  const total = jeong + pyeon;
  if (total === 0) return '부재';
  if (jeong > 0 && pyeon > 0) return '혼잡';
  if (total >= 3) return '과다';
  return '건전';
}
